/* import __COLOCATED_TEMPLATE__ from './fin-readiness.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import type ThemeService from 'embercom/services/theme-service';
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { FinGettingStartedState } from 'embercom/lib/reporting/fin-getting-started-states';

interface Signature {
  Element: HTMLDivElement;
  Args: {};
}

export default class FinReadiness extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare themeService: ThemeService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;

  @tracked totalVolume = '';
  @tracked selectedIndustry = 'unknown-industry';
  @tracked gettingStartedStateValue: 'personalized' | 'generic' | null = null;
  gettingStartedState = new FinGettingStartedState(this);

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadGettingStartedState();
  }

  async loadGettingStartedState() {
    try {
      this.gettingStartedStateValue = await this.gettingStartedState.getState();
    } catch (e) {
      console.error(e);
      this.gettingStartedStateValue = null;
    }
  }

  get shouldShowPersonalized(): boolean {
    return this.gettingStartedStateValue === 'personalized';
  }

  get intro() {
    if (this.shouldShowPersonalized) {
      return this.intl.t('automation.ai-insights.fin-readiness.intro', {
        appName: this.appService.app.name,
        htmlSafe: true,
      });
    }
    return this.intl.t('automation.ai-insights.fin-readiness.generic-intro', { htmlSafe: true });
  }

  get automationOpportunityTitle() {
    if (this.shouldShowPersonalized) {
      return this.intl.t('automation.ai-insights.fin-readiness.automation-opportunity.title');
    }
    return this.intl.t('automation.ai-insights.fin-readiness.automation-opportunity.generic-title');
  }

  get serializedReport() {
    let range;
    if (this.appService.app.hasGettingStartedBackfill) {
      range = 'past_12_months';
    } else {
      range = 'past_4_weeks';
    }

    return {
      id: 'fin-readiness',
      date_range: {
        selection: range,
      },
    };
  }

  @action
  setTotalVolume(totalVolume: string) {
    this.totalVolume = totalVolume;
  }

  @action
  onIndustryChange(selectedItem: string) {
    this.selectedIndustry = selectedItem;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'select_your_industry_button',
      place: 'get-started',
      section: 'select_your_industry',
      context: 'automation.get-started.select-your-industry',
      selectedIndustry: selectedItem,
      is_personalized: this.shouldShowPersonalized,
    });
  }

  getIndustryIcon(industryValue: string): InterfaceIconName {
    let iconMap: Record<string, InterfaceIconName> = {
      'unknown-industry': 'globe',
      'software-technology': 'multiplatform',
      'gaming-gambling': 'click-element',
      ecommerce: 'shopping-cart',
      'financial-services': 'cash',
    };
    return iconMap[industryValue] || 'globe';
  }

  get selectedIndustryText() {
    let selectedOption = this.industryOptions.find(
      (option) => option.value === this.selectedIndustry,
    );
    return selectedOption?.text || '';
  }

  get industryOptions() {
    return [
      {
        value: 'unknown-industry',
        text: this.intl.t(
          'automation.ai-insights.fin-readiness.personalized-conversations.industries.unknown-industry',
        ),
      },
      {
        value: 'software-technology',
        text: this.intl.t(
          'automation.ai-insights.fin-readiness.personalized-conversations.industries.software-technology',
        ),
      },
      {
        value: 'gaming-gambling',
        text: this.intl.t(
          'automation.ai-insights.fin-readiness.personalized-conversations.industries.gaming-gambling',
        ),
      },
      {
        value: 'ecommerce',
        text: this.intl.t(
          'automation.ai-insights.fin-readiness.personalized-conversations.industries.ecommerce',
        ),
      },
      {
        value: 'financial-services',
        text: this.intl.t(
          'automation.ai-insights.fin-readiness.personalized-conversations.industries.financial-services',
        ),
      },
    ];
  }

  get finIconUrl() {
    if (this.themeService.darkModeEnabled) {
      return assetUrl('/assets/images/automation/fin-readiness/fin-icon-dark-mode.svg');
    }
    return assetUrl('/assets/images/automation/fin-readiness/fin-icon-light-mode.svg');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadiness': typeof FinReadiness;
  }
}
